/**
 * QuickEntrySection — Dashboard orienté action pour agriculteurs pressés.
 * - Bandeau "X brouillons à valider" (lien direct vers le registre filtré)
 * - Dernières parcelles travaillées avec bouton "+ Traitement" (ouvre le wizard direct)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, AlertTriangle, Plus, ChevronRight } from "lucide-react";
import { authFetch } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function QuickEntrySection() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authFetch(`${API}/phyto-register/quick-stats`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) setStats(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!stats) return null;
  const { draft_count = 0, drafts_by_parcel = [], recent_parcels = [] } = stats;
  if (draft_count === 0 && recent_parcels.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="quick-entry-section">
      {/* Bandeau brouillons à valider */}
      {draft_count > 0 && (
        <div className="p-3 rounded-xl border flex items-start gap-3 flex-wrap" style={{ background: "#FFFBEB", borderColor: "#F59E0B40" }} data-testid="drafts-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#D97706" }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: "#92400E" }}>
              {draft_count} brouillon{draft_count > 1 ? "s" : ""} de traitement à valider dans votre registre
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {drafts_by_parcel.map(d => (
                <button
                  key={d.parcel_id}
                  onClick={() => navigate(`/carnet/${encodeURIComponent(d.parcel_id)}?tab=phyto&drafts=1`)}
                  data-testid={`draft-parcel-${d.parcel_id}`}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors flex items-center gap-1"
                >
                  {d.name || d.parcel_id} · {d.count} <ChevronRight className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Saisie rapide : dernières parcelles travaillées */}
      {recent_parcels.length > 0 && (
        <div className="p-4 rounded-2xl border bg-white" style={{ borderColor: "#0000000D" }} data-testid="quick-entry-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#2D6A4F18" }}>
              <Zap className="w-4 h-4" style={{ color: "#2D6A4F" }} />
            </span>
            <div>
              <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Saisie rapide</p>
              <p className="text-[11px]" style={{ color: "#4A4A4A" }}>Reprenez là où vous vous êtes arrêté</p>
            </div>
          </div>
          <div className="space-y-2">
            {recent_parcels.map(p => (
              <div key={p.parcel_id} className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: "#0000000D" }}>
                <button
                  onClick={() => navigate(`/carnet/${encodeURIComponent(p.parcel_id)}?tab=phyto`)}
                  data-testid={`recent-parcel-${p.parcel_id}`}
                  className="flex-1 min-w-0 text-left"
                >
                  <p className="text-sm font-semibold truncate" style={{ color: "#1A1A1A" }}>{p.name || p.parcel_id}</p>
                  <p className="text-[11px] truncate" style={{ color: "#4A4A4A" }}>
                    {p.last_product ? `Dernier : ${p.last_product}` : "—"}{p.last_date ? ` · ${p.last_date}` : ""}
                  </p>
                </button>
                <button
                  onClick={() => navigate(`/carnet/${encodeURIComponent(p.parcel_id)}?tab=phyto&new=1`)}
                  data-testid={`quick-treatment-${p.parcel_id}`}
                  className="shrink-0 flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-full text-white transition-all hover:shadow-md"
                  style={{ background: "#2D6A4F" }}
                >
                  <Plus className="w-3 h-3" /> Traitement
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
